<template>
  <div class="login-container">
    <div class="login-card">
      <div class="login-logo">
        <img :src="logoUrl" alt="Tattle" class="logo-image" />
      </div>

      <v-divider class="login-divider" />

      <!-- Already signed in — choose whether to continue or switch user -->
      <div v-if="isAuthenticated" class="okta-login-section">
        <div class="okta-header">
          <h3 class="okta-title">You're already signed in</h3>
          <p class="okta-subtitle">
            Continue as <strong>{{ currentUserLabel }}</strong> or sign in as a different user.
          </p>
        </div>

        <div class="okta-actions session-actions">
          <WButton
            label="Continue"
            variant="primary"
            section-tag="adx-login-continue"
            class="session-button"
            @click="handleContinue"
          />
          <WButton
            label="Sign in as a different user"
            variant="tertiary"
            :loading="switchingUser"
            section-tag="adx-login-switch-user"
            class="session-button"
            @click="handleSwitchUser"
          />
        </div>
      </div>

      <!-- Not signed in — show Okta SSO entry point -->
      <div v-else class="okta-login-section">
        <div class="okta-header">
          <h3 class="okta-title">Single Sign-On</h3>
          <p class="okta-subtitle">Use your company credentials to sign in</p>
        </div>

        <div class="okta-actions">
          <WButton
            label="Sign in with Okta"
            variant="secondary"
            :loading="loading"
            section-tag="adx-okta-login"
            class="okta-button"
            @click="handleLogin"
          />
        </div>
      </div>

      <div v-if="errorMessage" class="login-error">
        <div class="login-error-message">
          <span>{{ errorMessage }}</span>
          <WButton variant="icon" :icon="mdiClose" size="small" section-tag="login-dismiss-error" @click="errorMessage = ''" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'

import { useAuthStore } from '@/stores/auth.store'

import { useAuth } from '@/composables/useAuth'

import WButton from '@wasabi/actions/WButton.vue'

import { mdiClose } from '@mdi/js'

import logoUrl from '@/assets/images/logo-blue.svg'

// ============ ROUTER ============
const router = useRouter()

// ============ STORES ============
const authStore = useAuthStore()

// ============ COMPOSABLES ============
const auth = useAuth()

// ============ REACTIVE STATE ============
const loading = ref(false)
const switchingUser = ref(false)
const errorMessage = ref('')

// ============ COMPUTED PROPERTIES ============
const isAuthenticated = computed(() => authStore.isAuthenticated)
const currentUserLabel = computed(
  () => authStore.user?.name || authStore.user?.email || 'this account'
)

// ============ METHODS / EVENT HANDLERS ============
const handleLogin = async () => {
  loading.value = true
  errorMessage.value = ''

  try {
    await auth.signInWithRedirect()
  } catch (error) {
    console.error('[Login] signInWithRedirect failed:', error)
    errorMessage.value = 'Failed to initiate login. Please try again.'
    loading.value = false
  }
}

const handleContinue = () => {
  router.push('/admin/dashboard')
}

const handleSwitchUser = async () => {
  switchingUser.value = true
  errorMessage.value = ''

  try {
    // Logs out of Cognito and redirects back to /admin/login unauthenticated.
    await authStore.logout()
  } catch (error) {
    console.error('[Login] logout failed:', error)
    errorMessage.value = 'Failed to sign out. Please try again.'
    switchingUser.value = false
  }
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 20px;
}

.login-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  padding: 40px;
  width: 100%;
  max-width: 400px;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.login-logo {
  text-align: center;
  margin-bottom: 32px;
}

.logo-image {
  height: 56px;
  width: auto;
}

.login-divider {
  margin-bottom: 32px;
}

.okta-login-section {
  width: 100%;
  margin-bottom: 24px;
}

.okta-header {
  text-align: center;
  margin-bottom: 16px;
}

.okta-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 4px 0;
  line-height: 1.3;
}

.okta-subtitle {
  font-size: 13px;
  color: #666;
  margin: 0;
  font-weight: 400;
}

.okta-actions {
  width: 100%;
}

.okta-button {
  width: 100%;
  height: 48px;
  font-weight: 600;
  font-size: 16px;
  text-transform: none;
  letter-spacing: 0;
  border: 2px solid #1976d2;
}

.session-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.session-button {
  width: 100%;
}

.login-error {
  margin-top: 24px;
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .login-container {
    padding: 16px;
  }

  .login-card {
    padding: 24px;
    max-width: none;
  }

  .login-title {
    font-size: 24px;
  }
}

.login-error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 14px;
}

.login-error-message span {
  flex: 1;
}
</style>
